/*
 * @Author: CN\yangtianyu01 yangtianyu01@corp.netease.com
 * @Date: 2025-11-05 16:24:13
 * @LastEditors: CN\yangtianyu01 yangtianyu01@corp.netease.com
 * @LastEditTime: 2025-11-17 20:18:04
 * @FilePath: \mpc-monorepo\features\common\src\components\CellDescriptions\index.tsx
 * @Description: 这是默认设置,请设置`customMade`, 打开koroFileHeader查看配置 进行设置: https://github.com/OBKoro1/koro1FileHeader/wiki/%E9%85%8D%E7%BD%AE
 */
import { Button, Popover, Descriptions } from '@bedrock/components';
import React, { useMemo } from 'react';
import { DescriptionsProps } from '@bedrock/components/lib/Descriptions';
import { DescriptionsItemType } from '@bedrock/components/lib/Descriptions/Item';
import { PopoverProps } from '@bedrock/components/lib/Popover';
import { ChevronRight } from '@bedrock/icons-react';
import classNames from 'classnames';
import classNamesBind from 'classnames/bind';
import styles from './index.less';
import { useScreenAdapterContext } from '@bedrock/eed-react';
import { EllipsisTooltip } from '#components/EllipsisTooltip';

const cx = classNamesBind.bind(styles);

const CustomCellDescriptions: React.FC<DescriptionsProps> = (props) => {
  return (
    <Descriptions
      {...props}
      className={classNames(cx('custom-cell-descriptions', {'custom-cell-descriptions-no-colon': props.colon === false}), props.className)}
    />
  );
};

export interface PopoverDescriptionsProps {
  descriptionsProps?: DescriptionsProps;
  popoverProps?: PopoverProps;
}

export const PopoverDescriptions = (props: PopoverDescriptionsProps) => {
  return (
    <Popover
      content={<CustomCellDescriptions {...props?.descriptionsProps} />}
      {...props?.popoverProps}
      overlayClassName={classNames(
        cx('popover-cell-descriptions'),
        props?.popoverProps?.overlayClassName
      )}
      placement={props?.popoverProps?.placement || 'bottomLeft'}
    >
      <Button
        suffixIcon={<ChevronRight />}
        size="xSmall"
        className={'text-[12px] leading-[18px] h-[20px] rounded-[4px]'}
      >
        +{props?.descriptionsProps?.items?.length}
      </Button>
    </Popover>
  );
};

export interface CellDescriptionsProps
  extends Omit<DescriptionsProps, 'items'> {
  items: DescriptionsItemType[];
  maxDisplayCount?: number;
  matchFn?: (item: DescriptionsItemType, index: number) => boolean;
  otherLabel?: string;
  popoverProps?: PopoverProps;
  /**
   * 额外操作节点，渲染在数据行末尾的同一行内，用于贴近「+N」折叠按钮排列。
   *
   * 行为：
   * - 当存在折叠（remainingItems.length > 0）时：与「+N」Popover 按钮在同一
   *   item.children 内水平 inline 排列（+N 在左、extra 在右），形成
   *   「+2  📝 编辑」之类的横向操作区。
   * - 当无折叠时：作为独立 item 追加到末尾（独占一行，左对齐），
   *   形成「（指标行 1）/（指标行 2）/ 📝 编辑」的纵向布局。
   *
   * 典型场景：表格单元格内多行数据 + 行级操作按钮（如「编辑」「删除」）。
   */
  extra?: React.ReactNode;
}

export const CellDescriptions: React.FC<CellDescriptionsProps> = ({
  items = [],
  maxDisplayCount = 2,
  matchFn,
  otherLabel = '其他',
  popoverProps,
  extra,
  ...restProps
}) => {
  const { viewBase } = useScreenAdapterContext();

  const displayedIndices = useMemo(() => {
    if (items.length === 0) return [];

    if (matchFn) {
      const matchedIndex = items.findIndex((item, index) =>
        matchFn(item, index)
      );
      if (matchedIndex !== -1) return [matchedIndex];
    }

    return Array.from(
      { length: Math.min(maxDisplayCount, items.length) },
      (_, i) => i
    );
  }, [items, maxDisplayCount, matchFn]);

  const displayedItems = useMemo(
    () => displayedIndices.map((index) => items[index]),
    [items, displayedIndices]
  );
  const remainingItems = useMemo(
    () => items.filter((_, index) => !displayedIndices.includes(index)),
    [items, displayedIndices]
  );

  if (items.length === 0) {
    // 即使没有数据，只要传了 extra 也应该把 extra 渲染出来（避免空 cell 丢失操作入口）。
    if (extra) {
      return (
        <CustomCellDescriptions
          column={1}
          {...restProps}
          items={[
            {
              label: null,
              children: extra,
            } as DescriptionsItemType,
          ]}
        />
      );
    }
    return null;
  }

  /**
   * 折叠按钮（+N）与 extra 操作区的同行渲染。
   *
   * - hasOverflow=true：「+N」+ extra 同行 inline 排列（用 inline-flex 控制间距）
   * - hasOverflow=false 且 extra 存在：仅 extra 单独成行
   * - hasOverflow=true 且无 extra：仅「+N」原样输出（保持旧行为兼容）
   */
  const hasOverflow = remainingItems.length > 0;
  const overflowOrExtraItem: DescriptionsItemType | null = (() => {
    if (hasOverflow) {
      const popoverNode = (
        <PopoverDescriptions
          descriptionsProps={{
            column: 1,
            labelStyle: {
              maxWidth: 200 * viewBase,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
            ...restProps,
            items: remainingItems,
          }}
          popoverProps={popoverProps}
        />
      );
      return {
        label: otherLabel,
        children: extra ? (
          <span className="inline-flex items-center gap-2">
            {popoverNode}
            {extra}
          </span>
        ) : (
          popoverNode
        ),
      } as DescriptionsItemType;
    }
    if (extra) {
      return {
        label: null,
        children: extra,
      } as DescriptionsItemType;
    }
    return null;
  })();

  const finalItems: DescriptionsItemType[] = [
    ...displayedItems,
    ...(overflowOrExtraItem ? [overflowOrExtraItem] : []),
  ].map(n => {
    return {
      ...n,
      label: n.label ? <span title={n.label?.toString() || ''}>{n.label}</span> : null,
      children: typeof n.children === 'string' ? <EllipsisTooltip clamp={1} text={n.children} /> : n.children,
    }
  });

  return (
    <CustomCellDescriptions column={1} {...restProps} items={finalItems} />
  );
};
